import { ImportActionType } from "../const/import-action-type";
import { ImportFieldType, SUBSCRIPTION_STATUS_FIELDS } from "../const/field-type";
import { SubscriptionSource } from "../const/subscription-source";

function mapFieldToOption(field, selectedFields) {
  return {
    id: field,
    label: `import.mappings.fields.${field}`,
    hidden: field !== ImportFieldType.IGNORE && selectedFields.includes(field),
  };
}

function filterSubscriptionFieldsBySource(fields, source, excludedField) {
  if (source === SubscriptionSource.Manual && excludedField) {
    return fields.filter((field) => field !== excludedField);
  }
  return fields;
}

function filterFieldsForDefaultAction(fields, source, hasMappingSuggestionError) {
  if (!hasMappingSuggestionError && (source === SubscriptionSource.Manual || source === SubscriptionSource.NoUpdate)) {
    return fields.filter((field) => !SUBSCRIPTION_STATUS_FIELDS.includes(field));
  }
  return fields;
}

export const importGetters = {
  isFileMissing: (state) => state.fileToUpload == null,
  importPreparing: (state) => state.importPreparing,
  mappedFields: (state) => state.fieldMappings.selectedFields,
  treatNegativeStatusAsUnknown: (state) => state.treatNegativeStatusAsUnknown,
  hasNegativeSubscriptionValues: (state) => state.hasNegativeSubscriptionValues,
  hasMappingSuggestionError: (state) => state.fieldMappings.hasMappingSuggestionError,
  isColumnSelected: (state) => (index) => {
    const field = state.fieldMappings.selectedFields[index];
    return field !== undefined && field !== ImportFieldType.IGNORE;
  },
  isMappingPreviewLoading: (state) => state.fieldMappings.isPreviewLoading,
  unmappedFields: (state) => {
    const mappings = state.fieldMappings;
    const source = state.subscriptions.source;
    const selectedFields = mappings.selectedFields;
    const hasMappingSuggestionError = mappings.hasMappingSuggestionError;

    let fields;
    switch (state.actionType) {
      case ImportActionType.UpdateEmailSubscribersStatus:
        fields = filterSubscriptionFieldsBySource(
          mappings.emailSubscribeFields,
          source,
          ImportFieldType.EMAIL_SUBSCRIPTION_STATUS
        );
        break;
      case ImportActionType.UpdateSMSSubscribersStatus:
        fields = filterSubscriptionFieldsBySource(
          mappings.smsSubscribeFields,
          source,
          ImportFieldType.SMS_SUBSCRIPTION_STATUS
        );
        break;
      case ImportActionType.AddUpdateTags:
        fields = mappings.tagsFields;
        break;
      default:
        fields = filterFieldsForDefaultAction(mappings.availableFields, source, hasMappingSuggestionError);
    }

    return fields.map((field) => mapFieldToOption(field, selectedFields));
  },
  fileName: (state) => {
    return state.fileToUpload ? state.fileToUpload.name : "";
  },
  fileSize: (state) => {
    return state.fileToUpload ? state.fileToUpload.size : "";
  },
  previewRows: (state) => {
    const rows = state.fieldMappings.previewRows || [];
    return state.skipFirstLine ? rows.slice(1) : rows;
  },
  tags: (state) => state.tags.importTags,
  subscriptionSource: (state) => state.subscriptions.source,
  subscriptionStatusEmail: (state) => state.subscriptions.email,
  subscriptionStatusSms: (state) => state.subscriptions.sms,
  subscriptionStatusWhatsapp: (state) => state.subscriptions.whatsapp,
  importHistory: (state) => state.importHistory,
  tagsAction: (state) => state.tags.action,
  hasEmailSubscriptionMapped: (state) =>
    state.fieldMappings.selectedFields.includes(ImportFieldType.EMAIL_SUBSCRIPTION_STATUS),
  hasSmsSubscriptionMapped: (state) =>
    state.fieldMappings.selectedFields.includes(ImportFieldType.SMS_SUBSCRIPTION_STATUS),
  hasWhatsappSubscriptionMapped: (state) =>
    state.fieldMappings.selectedFields.includes(ImportFieldType.WHATSAPP_SUBSCRIPTION_STATUS),
  hasAnySubscriptionMapped: (state, getters) =>
    getters.hasEmailSubscriptionMapped || getters.hasSmsSubscriptionMapped || getters.hasWhatsappSubscriptionMapped,
  mappingErrors: (state) => {
    const fields = state.fieldMappings.selectedFields;
    return {
      emailOrPhone: !fields.includes(ImportFieldType.EMAIL) && !fields.includes(ImportFieldType.PHONE),
      subscription:
        state.subscriptions.source === SubscriptionSource.File &&
        !fields.includes(ImportFieldType.SMS_SUBSCRIPTION_STATUS) &&
        !fields.includes(ImportFieldType.EMAIL_SUBSCRIPTION_STATUS) &&
        !fields.includes(ImportFieldType.WHATSAPP_SUBSCRIPTION_STATUS),
    };
  },
  hasMappingErrors: (state, getters) => Object.values(getters.mappingErrors).some((e) => Boolean(e)),
  wizardLinks: (state, getters) => {
    const hasFile = state.fileToUpload != null;
    const hasMapping = !getters.hasMappingErrors;
    return [
      {
        pathPattern: /\/app\/import\/v2\/upload/,
        href: "/app/import/v2/upload",
      },
      {
        pathPattern: /\/app\/import\/v2\/mapping/,
        href: hasFile ? "/app/import/v2/mapping" : "",
      },
      {
        pathPattern: /\/app\/import\/v2\/tags/,
        href: hasFile && hasMapping ? "/app/import/v2/tags" : "",
      },
      {
        pathPattern: /\/app\/import\/v2\/summary/,
        href: hasFile && hasMapping ? "/app/import/v2/summary" : "",
      },
    ];
  },
  checksum: (state) => state.checksum,
};
